import React, { memo, useEffect, useMemo, useRef, useState } from 'react';

import { alpha, Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';

import { Button } from '@components/button/index.ts';
import { ResizableTable, type ResizableTableColumn } from '@components/index.ts';
import { type GameSessionShot } from '@api/game-sessions-api.ts';
import { formatDistanceByCountry } from '@assets/helpers/utils/format-distance-by-country.ts';

import {
  buildFlightMatrix,
  type Camera,
  clampZoom,
  describeWind,
  extractTrajectory,
  flightEndIndex,
  GROUND_HALF_W,
  LABEL_W,
  PAD,
  polyline,
  project,
  type ShotPointRow,
  toNumber,
  type Vec3,
} from './helpers.ts';

export type ShotFlight3DProps = {
  shot?: GameSessionShot | null;
  countryIsoCode?: string | null;
};

type ShotPointsTableProps = {
  matrix: number[][];
  landingIndex: number;
  flightTimeS: number | null;
  rollTimeS: number | null;
  countryIsoCode?: string | null;
};
/** Renders point details independently from the camera state of the 3D scene. */
const ShotPointsTable = memo(
  ({ matrix, landingIndex, flightTimeS, rollTimeS, countryIsoCode }: ShotPointsTableProps) => {
    const { palette } = useTheme();

    const rows = useMemo<ShotPointRow[]>(
      () =>
        matrix.map((point, index) => {
          const [x, y, z] = point;
          const airborne = index <= landingIndex;
          const timeSeconds = airborne
            ? flightTimeS != null
              ? (flightTimeS * index) / Math.max(1, landingIndex)
              : null
            : flightTimeS != null && rollTimeS != null
              ? flightTimeS +
                (rollTimeS * (index - landingIndex)) / Math.max(1, matrix.length - 1 - landingIndex)
              : null;

          return {
            id: `${airborne ? 'flight' : 'roll'}-${index}`,
            index,
            phase: airborne ? 'flight' : 'roll',
            timeSeconds,
            x,
            y,
            z,
          };
        }),
      [matrix, landingIndex, flightTimeS, rollTimeS]
    );

    const pointColumns = useMemo<ResizableTableColumn<ShotPointRow>[]>(
      () => [
        {
          id: 'index',
          header: '#',
          size: 64,
          minSize: 56,
          align: 'left',
          headerAlign: 'left',
          renderCell: ({ index }) => (
            <Typography variant="body_14_24" color={palette.light_dark}>
              {index}
            </Typography>
          ),
        },
        {
          id: 'phase',
          header: 'Phase',
          size: 120,
          minSize: 96,
          align: 'left',
          headerAlign: 'left',
          renderCell: ({ phase }) => (
            <Typography variant="body_14_24" color={palette.dark}>
              {phase}
            </Typography>
          ),
        },
        {
          id: 'time',
          header: 'Time',
          size: 112,
          minSize: 96,
          align: 'right',
          headerAlign: 'right',
          renderCell: ({ timeSeconds }) => (
            <Typography variant="body_14_24" color={palette.dark}>
              {timeSeconds != null ? `${timeSeconds.toFixed(2)} s` : '—'}
            </Typography>
          ),
        },
        {
          id: 'x',
          header: 'X (lateral)',
          size: 160,
          minSize: 132,
          align: 'right',
          headerAlign: 'right',
          renderCell: ({ x }) => (
            <Typography variant="body_14_24" color={palette.dark}>
              {formatDistanceByCountry(countryIsoCode, x, 2)}
            </Typography>
          ),
        },
        {
          id: 'y',
          header: 'Y (height)',
          size: 160,
          minSize: 132,
          align: 'right',
          headerAlign: 'right',
          renderCell: ({ y }) => (
            <Typography variant="body_14_24" color={palette.dark}>
              {formatDistanceByCountry(countryIsoCode, y, 2)}
            </Typography>
          ),
        },
        {
          id: 'z',
          header: 'Z (downrange)',
          size: 176,
          minSize: 148,
          align: 'right',
          headerAlign: 'right',
          renderCell: ({ z }) => (
            <Typography variant="body_14_24" color={palette.dark}>
              {formatDistanceByCountry(countryIsoCode, z, 2)}
            </Typography>
          ),
        },
      ],
      [countryIsoCode, palette.dark, palette.light_dark]
    );

    return (
      <ResizableTable
        tableStorageId="shot-flight-points"
        columns={pointColumns}
        data={rows}
        getRowId={({ id }) => id}
        stickyHeader
        maxHeight={220}
        tableProps={{
          size: 'small',
          sx: { fontVariantNumeric: 'tabular-nums' },
        }}
        paperProps={{
          sx: {
            border: `1px solid ${palette.pale_accent}`,
            borderRadius: 2,
          },
        }}
        getRowProps={({ phase }) => ({
          sx: phase === 'roll' ? { backgroundColor: palette.backgroundColor } : undefined,
        })}
      />
    );
  }
);

ShotPointsTable.displayName = 'ShotPointsTable';

/**
 * Perspective 3D view of the same flight the radar card draws in 2D, built from
 * the validated API points returned by `buildFlightMatrix`.
 * Drag to orbit the camera. Rendered as plain SVG — no 3D engine dependency.
 */
export const ShotFlight3D = ({ shot, countryIsoCode }: ShotFlight3DProps) => {
  const { palette, typography } = useTheme();

  // One array for the whole path: flight, then bounces and roll-out.
  const matrix = useMemo(() => (shot ? buildFlightMatrix(shot) : []), [shot]);
  const landingIndex = useMemo(() => (shot ? flightEndIndex(shot, matrix) : 0), [shot, matrix]);

  const flight = useMemo<Vec3[]>(
    () => matrix.slice(0, landingIndex + 1).map(([x, y, z]) => ({ x, y, z })),
    [matrix, landingIndex]
  );
  // Starts on the landing point, so the two polylines join up.
  const roll = useMemo<Vec3[]>(
    () => matrix.slice(landingIndex).map(([x, y, z]) => ({ x, y, z })),
    [matrix, landingIndex]
  );

  const [showPoints, setShowPoints] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const trajectory = useMemo(() => (shot ? extractTrajectory(shot) : null), [shot]);
  const carryM =
    shot?.trajectory?.carry ??
    trajectory?.carryM ??
    toNumber(shot?.carry) ??
    (flight.length ? flight[flight.length - 1].z : 0);
  const apexM =
    shot?.trajectory?.apex ??
    trajectory?.apexM ??
    toNumber(shot?.apex) ??
    flight.reduce((acc, p) => Math.max(acc, p.y), 0);
  const flightTimeS = shot?.trajectory?.flight_time ?? trajectory?.flightTimeS ?? null;
  const rollTimeS = shot?.trajectory?.roll_time ?? null;
  const wind = shot?.trajectory?.wind ?? null;

  const copyPoints = () => {
    const json = JSON.stringify(matrix.map((p) => p.map((v) => Number(v.toFixed(3)))));
    navigator.clipboard?.writeText(json).then(
      () => setCopied(true),
      () => setCopied(false)
    );
  };

  /**
   * Bounding box of everything that gets drawn: the ground patch, the flight and
   * the roll. The camera orbits its centre, so the scene turns around the middle
   * of the picture instead of drifting across it.
   */
  const scene = useMemo(() => {
    const rest = roll.length
      ? roll[roll.length - 1]
      : flight.length
        ? flight[flight.length - 1]
        : { x: 0, y: 0, z: 0 };

    const maxZ = Math.max(rest.z * 1.1, 50);
    const topY = flight.reduce((acc, p) => Math.max(acc, p.y), 0);

    return {
      rest,
      maxZ,
      center: { x: 0, y: topY / 2, z: maxZ / 2 }, // ground patch is symmetric in x
      radius: Math.max(60, Math.hypot(maxZ / 2, GROUND_HALF_W, topY / 2)),
    };
  }, [flight, roll]);

  const [orbit, setOrbit] = useState({ yaw: 0.42, pitch: 0.3 });
  const [hover, setHover] = useState<{ phase: 'flight' | 'roll'; index: number } | null>(null);
  const drag = useRef<{ x: number; y: number; yaw: number; pitch: number } | null>(null);

  /**
   * View transform over the fitted projection. `zoom` crops the box; `center` is
   * where the box is centred, in projection units (the orbit target sits at 0,0).
   * Panning moves `center`, zooming keeps the point under the cursor in place.
   */
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState({ x: 0, y: 0 });
  const pan = useRef<{ x: number; y: number; cx: number; cy: number; unitsPerPx: number } | null>(
    null
  );
  const svgRef = useRef<SVGSVGElement | null>(null);

  /** Zoom around a fixed point (in projection units), leaving it under the cursor. */
  const zoomAt = (factor: number, anchor?: { x: number; y: number }) => {
    setZoom((prev) => {
      const next = clampZoom(prev * factor);
      const a = anchor;
      if (a && next !== prev) {
        const k = prev / next; // how much the visible box shrinks
        setCenter((c) => ({ x: a.x + (c.x - a.x) * k, y: a.y + (c.y - a.y) * k }));
      }
      return next;
    });
  };

  const resetView = () => {
    setZoom(1);
    setCenter({ x: 0, y: 0 });
    setOrbit({ yaw: 0.42, pitch: 0.3 });
  };

  /** Pointer position in projection units. */
  const toProjection = (clientX: number, clientY: number) => {
    const svg = svgRef.current;
    const ctm = svg?.getScreenCTM();
    if (!svg || !ctm) return null;
    const point = svg.createSVGPoint();
    point.x = clientX;
    point.y = clientY;
    return point.matrixTransform(ctm.inverse());
  };

  // Wheel must be a non-passive listener, otherwise preventDefault() is ignored
  // and the page scrolls behind the modal.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const cursor = toProjection(event.clientX, event.clientY);
      zoomAt(event.deltaY < 0 ? 1.12 : 1 / 1.12, cursor ?? undefined);
    };

    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, []);

  const cam: Camera = useMemo(
    () => ({
      yaw: orbit.yaw,
      pitch: orbit.pitch,
      dist: scene.radius * 2.1,
      target: scene.center,
    }),
    [orbit, scene]
  );

  const handleDown = (event: React.MouseEvent<SVGSVGElement>) => {
    // Middle button pans; left button orbits.
    if (event.button === 1) {
      event.preventDefault(); // suppress the browser's autoscroll cursor
      const svg = svgRef.current;
      const box = svg?.viewBox.baseVal;
      // With preserveAspectRatio="meet" the tighter axis sets the scale — use it,
      // or the picture would lag behind the cursor while panning.
      const unitsPerPx =
        svg && box && svg.clientWidth > 0 && svg.clientHeight > 0
          ? Math.max(box.width / svg.clientWidth, box.height / svg.clientHeight)
          : 1;

      pan.current = { x: event.clientX, y: event.clientY, cx: center.x, cy: center.y, unitsPerPx };
      setIsDragging(true);
      setHover(null);
      return;
    }

    if (event.button !== 0) return;
    drag.current = { x: event.clientX, y: event.clientY, yaw: orbit.yaw, pitch: orbit.pitch };
    setIsDragging(true);
  };

  /** Snap the pointer to the nearest sampled point on the flight or the roll. */
  const trackHover = (event: React.MouseEvent<SVGSVGElement>) => {
    const cursor = toProjection(event.clientX, event.clientY);
    if (!cursor) return;

    let best: { phase: 'flight' | 'roll'; index: number } | null = null;
    let bestDistSq = Infinity;

    const consider = (points: Vec3[], phase: 'flight' | 'roll') => {
      points.forEach((p, index) => {
        const screen = project(p, cam);
        if (!screen) return;
        const distSq = (screen.x - cursor.x) ** 2 + (screen.y - cursor.y) ** 2;
        if (distSq < bestDistSq) {
          bestDistSq = distSq;
          best = { phase, index };
        }
      });
    };

    consider(flight, 'flight');
    consider(roll, 'roll');

    // Snap radius in viewBox units, so it stays the same on screen at any zoom.
    const snap = 34 / zoom;
    setHover(bestDistSq <= snap * snap ? best : null);
  };

  const handleMove = (event: React.MouseEvent<SVGSVGElement>) => {
    const panning = pan.current;
    if (panning) {
      // Drag the scene with the cursor: 1 px of mouse = 1 px of picture.
      setCenter({
        x: panning.cx - (event.clientX - panning.x) * panning.unitsPerPx,
        y: panning.cy - (event.clientY - panning.y) * panning.unitsPerPx,
      });
      return;
    }

    const start = drag.current;
    if (start) {
      // The scene follows the cursor: dragging right swings the camera left, so the
      // ground turns to the right under the hand. Dragging down tips the view up,
      // towards a top-down look.
      const yaw = start.yaw - (event.clientX - start.x) * 0.006;
      // Keep the camera above the ground and below the zenith.
      const pitch = Math.min(1.2, Math.max(0.05, start.pitch + (event.clientY - start.y) * 0.004));
      setOrbit({ yaw, pitch });
      setHover(null);
      return;
    }
    trackHover(event);
  };

  const endDrag = () => {
    drag.current = null;
    pan.current = null;
    setIsDragging(false);
    setHover(null);
  };

  if (!flight.length) {
    return (
      <Stack gap={3} minWidth={0}>
        <Typography variant="title_accent_18_18" color={palette.dark}>
          3D flight
        </Typography>
        <Box
          sx={{
            px: 4,
            py: 8,
            borderRadius: 2,
            bgcolor: palette.backgroundColor,
            color: palette.light_dark,
            textAlign: 'center',
          }}
        >
          <Typography variant="body_14_24" color={palette.light_dark}>
            No launch data for this shot
          </Typography>
        </Box>
      </Stack>
    );
  }

  const landing = flight[flight.length - 1];
  const { rest, maxZ } = scene;

  // Ground grid: downrange lines every 50 m, plus the lateral edges and tee line.
  const step = 50;
  const zLines: number[] = [];
  for (let z = 0; z <= maxZ; z += step) zLines.push(z);

  const shadow = flight.map((p) => ({ x: p.x, y: 0, z: p.z }));
  const apexPoint = flight.reduce((acc, p) => (p.y > acc.y ? p : acc), flight[0]);

  const ballScreen = project(rest, cam);
  const landingScreen = project(landing, cam);
  const apexScreen = project(apexPoint, cam);
  const teeScreen = project({ x: 0, y: 0, z: 0 }, cam);

  /**
   * Wind arrow on the turf, beside the flight: it points the way the wind blows
   * (i.e. the way it pushes the ball), scaled so a stronger wind draws a longer
   * arrow. Direction comes from the cross/head components, so it always agrees
   * with the numbers in the footer.
   */
  const windArrow = (() => {
    if (!wind || wind.speed_mps <= 0) return null;

    const len = Math.min(35, 6 + wind.speed_mps * 2); // metres, clamped so it stays readable
    const dir = Math.hypot(wind.cross_mps, wind.head_mps);
    if (dir < 0.05) return null;

    const ux = wind.cross_mps / dir;
    const uz = wind.head_mps / dir;

    // Park it off to the side of the fairway, a third of the way downrange.
    const anchor = { x: -GROUND_HALF_W * 0.65, y: 0, z: scene.maxZ * 0.32 };
    const from = { x: anchor.x - (ux * len) / 2, y: 0, z: anchor.z - (uz * len) / 2 };
    const to = { x: anchor.x + (ux * len) / 2, y: 0, z: anchor.z + (uz * len) / 2 };

    // Arrow head: two barbs swept back from the tip.
    const barb = len * 0.28;
    const headLeft = {
      x: to.x - barb * (ux * 0.7 + uz * 0.7),
      y: 0,
      z: to.z - barb * (uz * 0.7 - ux * 0.7),
    };
    const headRight = {
      x: to.x - barb * (ux * 0.7 - uz * 0.7),
      y: 0,
      z: to.z - barb * (uz * 0.7 + ux * 0.7),
    };

    return { from, to, headLeft, headRight, anchor };
  })();

  const windLabelScreen = windArrow ? project(windArrow.anchor, cam) : null;

  // Fit the viewBox to everything that gets drawn (ground corners + flight + roll),
  // so the scene stays fully framed while the camera orbits.
  const framed = [
    ...flight,
    ...roll,
    { x: -GROUND_HALF_W, y: 0, z: 0 },
    { x: GROUND_HALF_W, y: 0, z: 0 },
    { x: -GROUND_HALF_W, y: 0, z: maxZ },
    { x: GROUND_HALF_W, y: 0, z: maxZ },
  ]
    .map((p) => project(p, cam))
    .filter((p): p is { x: number; y: number } => p != null);

  // The apex/total labels sit to the right of their anchors — frame their text box
  // too, instead of padding the whole scene rightwards (which would off-centre it).
  const labelled = [ballScreen, apexScreen]
    .filter((p): p is { x: number; y: number } => p != null)
    .map((p) => ({ x: p.x + LABEL_W, y: p.y }));

  // The camera target projects to (0, 0) by construction. Framing the box
  // symmetrically around it keeps the orbit centre in the centre of the picture,
  // so turning the camera spins the scene in place instead of swinging it around.
  const all = [...framed, ...labelled];
  const halfW = Math.max(...all.map((p) => Math.abs(p.x))) + PAD;
  const halfH = Math.max(...all.map((p) => Math.abs(p.y))) + PAD;

  // Zoom crops that fitted box; panning slides `center` away from the orbit point.
  const boxW = (2 * halfW) / zoom;
  const boxH = (2 * halfH) / zoom;
  const boxX = center.x - boxW / 2;
  const boxY = center.y - boxH / 2;
  const viewBox = `${boxX.toFixed(1)} ${boxY.toFixed(1)} ${boxW.toFixed(1)} ${boxH.toFixed(1)}`;

  // The viewBox shrinks as we zoom, so stroke widths, dots and text are drawn in
  // viewBox units scaled by 1/zoom to keep a constant size on screen.
  const s = 1 / zoom;

  // Hovered sample → marker position and the numbers next to it: downrange, height,
  // side and time, the same reading the 2D radar card gives.
  const tip = (() => {
    if (!hover) return null;

    const points = hover.phase === 'flight' ? flight : roll;
    const p = points[hover.index];
    const at = p ? project(p, cam) : null;
    if (!p || !at) return null;

    const timeS =
      hover.phase === 'flight'
        ? flightTimeS != null
          ? (flightTimeS * hover.index) / Math.max(1, flight.length - 1)
          : null
        : flightTimeS != null && rollTimeS != null
          ? flightTimeS + (rollTimeS * hover.index) / Math.max(1, roll.length - 1)
          : null;

    const lines = [
      `→ ${formatDistanceByCountry(countryIsoCode, p.z)} downrange`,
      `↑ ${formatDistanceByCountry(countryIsoCode, p.y)} height`,
      `${p.x < 0 ? 'L' : 'R'} ${formatDistanceByCountry(countryIsoCode, Math.abs(p.x))} side`,
    ];
    if (timeS != null) lines.push(`⏱ ${timeS.toFixed(2)} s`);
    if (hover.phase === 'roll') lines.push('after landing');

    return { at, lines };
  })();

  return (
    <Stack gap={3} minWidth={0}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        gap={2}
      >
        <Typography variant="title_accent_18_18" color={palette.dark}>
          3D flight
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="small_description" color={palette.light_dark}>
            drag to orbit · wheel to zoom · middle-drag to pan
          </Typography>
          <Button
            variantStyle="secondary"
            size="small"
            aria-label="Zoom out"
            onClick={() => zoomAt(1 / 1.25)}
            sx={{ minWidth: 32, px: 2 }}
          >
            −
          </Button>
          <Typography
            variant="small_description"
            color={palette.light_dark}
            sx={{ minWidth: 38, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
          >
            {zoom.toFixed(1)}×
          </Typography>
          <Button
            variantStyle="secondary"
            size="small"
            aria-label="Zoom in"
            onClick={() => zoomAt(1.25)}
            sx={{ minWidth: 32, px: 2 }}
          >
            +
          </Button>
          <Button variantStyle="secondary" size="small" onClick={resetView}>
            Reset
          </Button>
        </Stack>
      </Stack>

      <Box
        sx={{
          position: 'relative',
          height: 340,
          borderRadius: 2,
          bgcolor: palette.backgroundColor,
          p: 1,
          overflow: 'hidden',
          userSelect: 'none',
        }}
      >
        <svg
          ref={svgRef}
          viewBox={viewBox}
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMid meet"
          style={{
            display: 'block',
            cursor: isDragging ? 'grabbing' : 'grab',
            fontFamily: typography.fontFamily,
          }}
          onMouseDown={handleDown}
          onMouseMove={handleMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
        >
          {zLines.map((z) => (
            <polyline
              key={`z-${z}`}
              points={polyline(
                [
                  { x: -GROUND_HALF_W, y: 0, z },
                  { x: GROUND_HALF_W, y: 0, z },
                ],
                cam
              )}
              fill="none"
              stroke={z === 0 ? palette.accent : palette.pale_accent}
              strokeWidth={z === 0 ? 1.6 : 1}
            />
          ))}

          {[-GROUND_HALF_W, -GROUND_HALF_W / 2, 0, GROUND_HALF_W / 2, GROUND_HALF_W].map((x) => (
            <polyline
              key={`x-${x}`}
              points={polyline(
                [
                  { x, y: 0, z: 0 },
                  { x, y: 0, z: maxZ },
                ],
                cam
              )}
              fill="none"
              stroke={x === 0 ? palette.accent : palette.pale_accent}
              strokeWidth={x === 0 ? 1.6 : 1}
              strokeDasharray={x === 0 ? undefined : '4 6'}
            />
          ))}

          <polyline
            points={polyline(shadow, cam)}
            fill="none"
            stroke={palette.pale_accent}
            strokeWidth={2 * s}
            strokeDasharray={`${5 * s} ${5 * s}`}
          />

          {/* Apex drop line — reads the height against the ground. */}
          <polyline
            points={polyline([apexPoint, { x: apexPoint.x, y: 0, z: apexPoint.z }], cam)}
            fill="none"
            stroke={palette.light_dark}
            strokeWidth={1.2 * s}
            strokeDasharray={`${3 * s} ${4 * s}`}
          />

          <polyline
            points={polyline(flight, cam)}
            fill="none"
            stroke={palette.accent_text}
            strokeWidth={2.5 * s}
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Bounces + roll-out: the ball keeps going after it lands. */}
          {roll.length > 1 && (
            <polyline
              points={polyline(roll, cam)}
              fill="none"
              stroke={palette.accent}
              strokeWidth={1.8 * s}
              strokeDasharray={`${6 * s} ${3 * s}`}
              strokeLinecap="round"
            />
          )}

          {/* Wind arrow: drawn on the turf, pointing the way the wind blows. */}
          {windArrow && (
            <g>
              <polyline
                points={polyline([windArrow.from, windArrow.to], cam)}
                fill="none"
                stroke={palette.neon_blue_100}
                strokeWidth={2 * s}
                strokeLinecap="round"
              />
              <polyline
                points={polyline([windArrow.headLeft, windArrow.to, windArrow.headRight], cam)}
                fill="none"
                stroke={palette.neon_blue_100}
                strokeWidth={2 * s}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {windLabelScreen && (
                <text
                  x={windLabelScreen.x + 8 * s}
                  y={windLabelScreen.y - 6 * s}
                  fill={palette.neon_blue_100}
                  fontSize={12 * s}
                  fontWeight={600}
                >
                  wind {wind!.speed_mps.toFixed(1)} m/s
                </text>
              )}
            </g>
          )}

          {teeScreen && (
            <circle cx={teeScreen.x} cy={teeScreen.y} r={3.5 * s} fill={palette.pale_accent} />
          )}

          {/* Landing mark stays visible even when the ball has rolled past it. */}
          {landingScreen && roll.length > 1 && (
            <circle
              cx={landingScreen.x}
              cy={landingScreen.y}
              r={4 * s}
              fill="none"
              stroke={palette.accent_text}
              strokeWidth={1.6 * s}
            />
          )}

          {apexScreen && (
            <text
              x={apexScreen.x + 8 * s}
              y={apexScreen.y - 6 * s}
              fill={palette.dark}
              fontSize={13 * s}
              fontWeight={600}
            >
              apex {formatDistanceByCountry(countryIsoCode, apexM)}
            </text>
          )}

          {ballScreen && (
            <>
              <circle
                cx={ballScreen.x}
                cy={ballScreen.y}
                r={7 * s}
                fill={palette.white_100}
                stroke={palette.accent_text}
                strokeWidth={2.5 * s}
              />
              <text
                x={ballScreen.x + 12 * s}
                y={ballScreen.y + 4 * s}
                fill={palette.dark}
                fontSize={13 * s}
                fontWeight={600}
              >
                {roll.length > 1
                  ? `total ${formatDistanceByCountry(countryIsoCode, rest.z, 0)}`
                  : `carry ${formatDistanceByCountry(countryIsoCode, carryM, 0)}`}
              </text>
            </>
          )}

          {tip && (
            <Tooltip
              open
              placement="top"
              disableFocusListener
              disableHoverListener
              disableTouchListener
              disableInteractive
              title={
                <Stack gap={0.5}>
                  {tip.lines.map((line) => (
                    <Box component="span" key={line}>
                      {line}
                    </Box>
                  ))}
                </Stack>
              }
              slotProps={{
                tooltip: {
                  sx: {
                    px: 1.5,
                    py: 0.8,
                    borderRadius: '14px',
                    bgcolor: palette.white_100,
                    border: `1px solid ${alpha(palette.accent_text, 0.35)}`,
                    boxShadow: `0 8px 24px ${palette.black_15}`,
                    color: palette.dark,
                    fontSize: 16,
                    lineHeight: 1,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                  },
                },
              }}
            >
              <circle
                cx={tip.at.x}
                cy={tip.at.y}
                r={5.5 * s}
                fill={palette.white_100}
                stroke={palette.dark}
                strokeWidth={2 * s}
              />
            </Tooltip>
          )}
        </svg>
      </Box>

      <Stack
        direction="row"
        flexWrap="wrap"
        columnGap={6}
        rowGap={2}
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: palette.white_100,
          color: palette.dark,
        }}
      >
        <Typography variant="body_14_24" color={palette.dark} fontWeight={600}>
          Carry: {formatDistanceByCountry(countryIsoCode, carryM, 0)}
        </Typography>
        <Typography variant="body_14_24" color={palette.dark} fontWeight={600}>
          Apex: {formatDistanceByCountry(countryIsoCode, apexM)}
        </Typography>
        <Typography variant="body_14_24" color={palette.dark} fontWeight={600}>
          Flight: {flightTimeS != null ? `${flightTimeS.toFixed(1)} s` : '-'}
        </Typography>
        <Typography variant="body_14_24" color={palette.dark} fontWeight={600}>
          Side: {landing.x < 0 ? 'L' : 'R'}{' '}
          {formatDistanceByCountry(countryIsoCode, Math.abs(landing.x))}
        </Typography>
        {roll.length > 1 && (
          <>
            <Typography variant="body_14_24" color={palette.dark} fontWeight={600}>
              Bounces: {shot?.trajectory?.bounces ?? 0} · +
              {formatDistanceByCountry(countryIsoCode, rest.z - landing.z, 0)} runout
            </Typography>
            <Typography variant="body_14_24" color={palette.dark} fontWeight={600}>
              Total: {formatDistanceByCountry(countryIsoCode, rest.z, 0)}
            </Typography>
          </>
        )}
        <Typography
          variant="body_14_24"
          color={wind ? palette.dark : palette.light_dark}
          fontWeight={600}
        >
          {wind
            ? `Wind: ${wind.speed_mps.toFixed(1)} m/s · ${describeWind(wind)}`
            : 'Wind: no data'}
        </Typography>
      </Stack>

      <Stack gap={2} minWidth={0}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          gap={2}
        >
          <Button
            variantStyle="secondary"
            size="small"
            onClick={() => setShowPoints((open) => !open)}
            sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
          >
            {showPoints ? 'Hide' : 'Show'} points [x, y, z] · {matrix.length}
            {roll.length > 1
              ? ` (${landingIndex + 1} flight + ${matrix.length - landingIndex - 1} bounce/roll)`
              : ''}{' '}
            · from API
          </Button>
          <Button
            variantStyle="secondary"
            size="small"
            onClick={copyPoints}
            sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
          >
            {copied ? 'Copied' : 'Copy JSON'}
          </Button>
        </Stack>

        {showPoints && (
          <ShotPointsTable
            matrix={matrix}
            landingIndex={landingIndex}
            flightTimeS={flightTimeS}
            rollTimeS={rollTimeS}
            countryIsoCode={countryIsoCode}
          />
        )}
      </Stack>
    </Stack>
  );
};
